import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { useForm } from 'react-hook-form';
import { addCrop, updateCrop, getLanguages, translateData, getCountries, getAllPhases } from '../lib/api';
import { toast } from 'react-toastify';

// ─── Constants ────────────────────────────────────────────────────────────────
const MAX_IMAGE_SIZE   = 1 * 1024 * 1024; // 1 MB
const MAX_OTHER_IMAGES = 5;
const IMG_URL          = 'https://api.soksan-dss.com/uploads/crop/';
const CATEGORY_OPTIONS = ['Crop', 'Fruits', 'Vegetables', 'Flowers', 'Others'];

// ─── helpers ─────────────────────────────────────────────────────────────
const validateImageFile = (file) => {
  if (!file) return null;
  if (file.size > MAX_IMAGE_SIZE) { toast('Image size should be less than 1MB'); return null; }
  return file;
};

const validateImageFiles = (files, currentCount = 0) => {
  const remaining = MAX_OTHER_IMAGES - currentCount;
  if (remaining <= 0) { toast(`Maximum ${MAX_OTHER_IMAGES} images already selected.`); return []; }
  for (const f of files) {
    if (f.size > MAX_IMAGE_SIZE) { toast('Image size should be less than 1MB'); return []; }
  }
  return files.slice(0, remaining);
};

const revokeBlob = (url) => { if (url?.startsWith('blob:')) URL.revokeObjectURL(url); };

/** Re-index an object map after removing key `removedIndex`. */
const reindexMap = (map, removedIndex) => {
  const next = {};
  Object.keys(map || {}).forEach((k) => {
    const n = parseInt(k, 10);
    if (n === removedIndex) return;
    const newKey = n > removedIndex ? n - 1 : n;
    const v = map[k];
    next[newKey] = Array.isArray(v) ? v.slice() : (v && typeof v === 'object') ? { ...v } : v;
  });
  return next;
};

const parseOtherLang = (data, fallback) => {
  try { return typeof data === 'string' ? JSON.parse(data) : data || {}; }
  catch { return typeof fallback === 'string' ? { en: fallback } : {}; }
};

const parseVariantOtherLang = (v) => {
  try {
    return {
      VariantName:        parseOtherLang(v.VariantNameOtherLang,   v.VariantName),
      VariantShortName:   parseOtherLang(v.VariantShorNameOtherLang, v.VariantShortName),
      VariantDescription: parseOtherLang(v.DescriptionOtherLang,   v.Description),
      VariantSignificance:parseOtherLang(v.SignificanceOtherLang,  v.Significance),
    };
  } catch {
    return {
      VariantName:        { en: v.VariantName   || '' },
      VariantShortName:   { en: v.VariantShortName || '' },
      VariantDescription: { en: v.Description   || '' },
      VariantSignificance:{ en: v.Significance  || '' },
    };
  }
};

const updateLangMap = (map, lang, value) => ({ ...(map || {}), [lang]: value });

// ─── Component ────────────────────────────────────────────────────────────────
const AddCropMaster = ({ isOpen, onClose, onSubmit, cropToEdit, onCropAdded }) => {
  const cropInputRef = useRef(null);

  // UI state
  const [loading,       setLoading]       = useState(false);
  const [message,       setMessage]       = useState(null);
  const [mainTab,       setMainTab]       = useState('english');
  const [step,          setStep]          = useState('idle');   // 'idle' | 'translating'
  const [hasTranslated, setHasTranslated] = useState(false);

  // Reference data
  const [languages,  setLanguages]  = useState([]);
  const [countries,  setCountries]  = useState([]);
  const [phasesList, setPhasesList] = useState([]);

  // Selected language for the "Other Languages" tab
  const [selectedLang, setSelectedLang] = useState('');
  const selectedLangName = useMemo(
    () => languages.find(l => l.GoogleLanguageCode === selectedLang)?.LanguageName || selectedLang || '',
    [languages, selectedLang],
  );

  // Crop-level country selection
  const [selectedCountries, setSelectedCountries] = useState([]);

  // Variants & related lists
  const [variants,         setVariants]         = useState([]);
  const [significanceList, setSignificanceList] = useState([]);
  const [phases,           setPhases]           = useState([]);
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);

  /**
   * variantCountries: { [variantIndex]: string[] }
   * Key is the STABLE index of the variant inside the `variants` array.
   * During edit we use `editingVariantIndex`; during add we use `variants.length`
   * (the index the new variant will occupy).
   */
  const [variantCountries, setVariantCountries] = useState({});

  // Crop-level images
  const [featureImage,        setFeatureImage]        = useState(null);
  const [featureImagePreview, setFeatureImagePreview] = useState(null);
  const [otherImages,         setOtherImages]         = useState([]);
  const [otherImagesPreviews, setOtherImagesPreviews] = useState([]);

  // Current-variant-form images (before "Add Variety" is clicked)
  const [cvFeatureImage,        setCvFeatureImage]        = useState(null);
  const [cvFeatureImagePreview, setCvFeatureImagePreview] = useState(null);
  const [cvOtherImages,         setCvOtherImages]         = useState([]);
  const [cvOtherImagesPreviews, setCvOtherImagesPreviews] = useState([]);

  /**
   * Persisted per-variant image maps (keyed by variant array index).
   * variantFeatureImages[i]         → File | null
   * variantFeatureImagesPreviews[i] → string (blob: or remote URL)
   * variantOtherImages[i]           → File[]
   * variantOtherImagesPreviews[i]   → string[]
   */
  const [variantFeatureImages,         setVariantFeatureImages]         = useState({});
  const [variantFeatureImagesPreviews, setVariantFeatureImagesPreviews] = useState({});
  const [variantOtherImages,           setVariantOtherImages]           = useState({});
  const [variantOtherImagesPreviews,   setVariantOtherImagesPreviews]   = useState({});

  // Translated / multilingual data
  const [categoryOL,    setCategoryOL]    = useState({});
  const [cropNameOL,    setCropNameOL]    = useState({});
  const [cropShortNameOL, setCropShortNameOL] = useState({});
  const [descriptionOL, setDescriptionOL] = useState({});
  const [significanceOL,setSignificanceOL]= useState({});
  const [variantsOL,    setVariantsOL]    = useState([]);

  // react-hook-form
  const { register, handleSubmit, formState: { errors }, reset, setValue, clearErrors, getValues, watch } = useForm({
    defaultValues: {
      category: '', cropName: '', cropShortName: '', description: '', isActive: true,
      variantName: '', variantShortName: '', variantDescription: '',
      significance: '', minGrowthPeriod: '', maxGrowthPeriod: '',
      phase: '', fromDay: '', toDay: '', yieldFrom: '', yieldTo: '',
    },
  });

  const significanceInput = watch('significance');
  const getToken = () => localStorage.getItem('authToken') || '';

  // ── Scroll lock ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'auto'; };
  }, [isOpen]);

  // ── Load reference data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const token    = getToken();
        const [langsData, phaseRes, countryRes] = await Promise.all([
          getLanguages(),
          getAllPhases(token),
          getCountries(),
        ]);
        const langList  = langsData?.data  || langsData  || [];
        const phaseData = phaseRes?.data?.phases || phaseRes || [];
        setLanguages(langList);
        setPhasesList(phaseData);
        setCountries(countryRes.countries || []);
        if (langList.length > 0) setSelectedLang(langList[0].GoogleLanguageCode);
      } catch (err) { console.error(err); }
    })();
  }, [isOpen]);

  // ── Populate form when editing / reset when adding ───────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => cropInputRef.current?.focus(), 100);

    if (cropToEdit) {
      setValue('category',      cropToEdit.Category      || '');
      setValue('cropName',      cropToEdit.CropName      || '');
      setValue('cropShortName', cropToEdit.CropShortName || '');
      setValue('description',   cropToEdit.Description   || '');
      setValue('isActive',      cropToEdit.IsActive      ?? false);
      clearErrors();

      setVariants(cropToEdit.Variants || []);
      setSignificanceList(cropToEdit.significanceList || []);
      setPhases(cropToEdit.phases || []);

      // Crop-level country selection
      try {
        const allowed = typeof cropToEdit.AllowedCountries === 'string'
          ? JSON.parse(cropToEdit.AllowedCountries)
          : cropToEdit.AllowedCountries || [];
        setSelectedCountries(allowed);
      } catch { setSelectedCountries([]); }

      // Crop-level image previews
      if (cropToEdit.FeatureImageFileName) {
        setFeatureImagePreview(`${IMG_URL}${cropToEdit.SystemFileName || cropToEdit.FeatureImageFileName}`);
      }
      if (cropToEdit.OtherImages?.length) {
        setOtherImagesPreviews(
          cropToEdit.OtherImages.map(img => `${IMG_URL}crop_other_img/${img.SystemFileName || img.OtherImageFileName}`)
        );
      }

      // Variant image previews
      const vfp = {};
      const vop = {};
      (cropToEdit.Variants || []).forEach((v, i) => {
        if (v.FeatureImage)
          vfp[i] = `${IMG_URL}VariantFeatureImages/${v.FeatureImage}`;
        if (v.VariantOtherImages?.length)
          vop[i] = v.VariantOtherImages.map(img => `${IMG_URL}VariantOtherImages/${img.SystemFileName || img.OtherImage}`);
      });
      setVariantFeatureImagesPreviews(vfp);
      setVariantOtherImagesPreviews(vop);

      // Variant country maps – build from existing variants
      const vcMap = {};
      (cropToEdit.Variants || []).forEach((v, i) => {
        try {
          const ac = typeof v.AllowedCountries === 'string'
            ? JSON.parse(v.AllowedCountries)
            : v.AllowedCountries || [];
          if (ac.length) vcMap[i] = ac;
        } catch { /* ignore */ }
      });
      setVariantCountries(vcMap);

      // OtherLang
      try {
        setCategoryOL(parseOtherLang(cropToEdit.CategoryOtherLang,    cropToEdit.Category));
        setCropNameOL(parseOtherLang(cropToEdit.CropNameOtherLang,     cropToEdit.CropName));
        setCropShortNameOL(parseOtherLang(cropToEdit.CropShortNameOtherLang, cropToEdit.CropShortName));
        setDescriptionOL(parseOtherLang(cropToEdit.DescriptionOtherLang, cropToEdit.Description));
        setSignificanceOL(parseOtherLang(cropToEdit.SignificanceOtherLang, ''));
        setVariantsOL((cropToEdit.Variants || []).map(parseVariantOtherLang));
      } catch { setVariantsOL((cropToEdit.Variants || []).map(parseVariantOtherLang)); }

      setHasTranslated(false);
      setMainTab('english');
    } else {
      fullReset();
    }
  }, [isOpen, cropToEdit]);

  // ── Full reset (new crop) ─────────────────────────────────────────────────────
  const fullReset = () => {
    reset();
    setValue('isActive', true);
    setVariants([]);
    setSignificanceList([]);
    setPhases([]);
    setSelectedCountries([]);
    setVariantCountries({});
    setEditingVariantIndex(null);

    setFeatureImage(null);        setFeatureImagePreview(null);
    setOtherImages([]);           setOtherImagesPreviews([]);
    setCvFeatureImage(null);      setCvFeatureImagePreview(null);
    setCvOtherImages([]);         setCvOtherImagesPreviews([]);
    setVariantFeatureImages({});  setVariantFeatureImagesPreviews({});
    setVariantOtherImages({});    setVariantOtherImagesPreviews({});

    setCategoryOL({}); setCropNameOL({}); setCropShortNameOL({});
    setDescriptionOL({}); setSignificanceOL({}); setVariantsOL([]);
    setHasTranslated(false);
    setMainTab('english');
  };

  // ── Variant form reset ────────────────────────────────────────────────────────
  const resetVariantForm = () => {
    setValue('variantName', '');        setValue('variantShortName', '');
    setValue('variantDescription', ''); setValue('minGrowthPeriod', '');
    setValue('maxGrowthPeriod', '');    setValue('yieldFrom', '');
    setValue('yieldTo', '');
    setSignificanceList([]); setPhases([]);
    setCvFeatureImage(null);      setCvFeatureImagePreview(null);
    setCvOtherImages([]);         setCvOtherImagesPreviews([]);
    clearErrors(['variantName', 'variantShortName', 'variantDescription']);
  };

  // ─── Image handlers ────────────────────────────────────────────────────────
  const handleSingleImageChange = useCallback((e, setFile, setPreview) => {
    const file = e.target.files?.[0] ? validateImageFile(e.target.files[0]) : null;
    if (file) { setFile(file); setPreview(URL.createObjectURL(file)); }
    else      { setFile(null); setPreview(null); e.target.value = ''; }
  }, []);

  const handleMultipleImagesChange = useCallback((e, setFiles, setPreviews, currentCount) => {
    if (!e.target.files?.length) return;
    const valid = validateImageFiles(Array.from(e.target.files), currentCount);
    if (!valid.length) { e.target.value = ''; return; }
    setFiles(prev  => [...prev,  ...valid]);
    setPreviews(prev => [...prev, ...valid.map(f => URL.createObjectURL(f))]);
  }, []);

  const removeMultipleImage = useCallback((index, previews, setFiles, setPreviews) => {
    revokeBlob(previews[index]);
    setFiles(prev    => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Crop-level
  const handleFeatureImageChange = (e) => handleSingleImageChange(e, setFeatureImage, setFeatureImagePreview);
  const handleOtherImagesChange  = (e) => handleMultipleImagesChange(e, setOtherImages, setOtherImagesPreviews, otherImages.length);
  const removeOtherImage         = (i) => removeMultipleImage(i, otherImagesPreviews, setOtherImages, setOtherImagesPreviews);

  // Current-variant-form
  const handleCvFeatureImageChange = (e) => handleSingleImageChange(e, setCvFeatureImage, setCvFeatureImagePreview);
  const handleCvOtherImagesChange  = (e) => handleMultipleImagesChange(e, setCvOtherImages, setCvOtherImagesPreviews, cvOtherImages.length);
  const removeCvOtherImage         = (i) => removeMultipleImage(i, cvOtherImagesPreviews, setCvOtherImages, setCvOtherImagesPreviews);

  // ── Significance ──────────────────────────────────────────────────────────────
  const handleAddSignificance = () => {
    const v = (significanceInput || '').trim();
    if (!v || v.includes('$')) { setMessage({ type: 'error', text: 'Significance cannot contain "$" and must be non-empty.' }); return; }
    setSignificanceList(prev => [...prev, v]);
    setValue('significance', '');
    clearErrors('significance');
    setHasTranslated(false); setMainTab('english');
  };

  const handleRemoveSignificance = (index) => {
    setSignificanceList(prev => prev.filter((_, i) => i !== index));
    setHasTranslated(false); setMainTab('english');
  };

  // ── Phases ────────────────────────────────────────────────────────────────────
  const handleAddPhase = () => {
    const data = getValues();
    if (!data.phase || !data.fromDay || !data.toDay) { toast('Please fill all phase fields.'); return; }
    const from = parseInt(data.fromDay), to = parseInt(data.toDay);
    if (from >= to) { toast('From Day must be less than To Day.'); return; }
    if (phases.some(p => p.PhaseId === data.phase)) { toast('This phase is already added.'); return; }
    const selected = phasesList.find(p => p.ID === data.phase);
    setPhases(prev => [...prev, { PhaseId: data.phase, PhaseName: selected?.PhaseName || data.phase, FromDays: from, ToDays: to }]);
    setValue('phase', ''); setValue('fromDay', ''); setValue('toDay', '');
    clearErrors(['phase', 'fromDay', 'toDay']);
  };

  const handleRemovePhase = (index) => setPhases(prev => prev.filter((_, i) => i !== index));

  // ── Variant validation helper ─────────────────────────────────────────────────
  const validateVariantFields = (data) => {
    const minGP = parseInt(data.minGrowthPeriod), maxGP = parseInt(data.maxGrowthPeriod);
    const yFrom = parseFloat(data.yieldFrom),     yTo   = parseFloat(data.yieldTo);
    if (!data.variantName || !data.variantShortName || !data.variantDescription ||
        !data.minGrowthPeriod || !data.maxGrowthPeriod || !data.yieldFrom || !data.yieldTo) {
      setMessage({ type: 'error', text: 'Please fill all required variant fields.' }); return null;
    }
    if (minGP >= maxGP) { toast('Minimum Growth Duration must be less than Maximum Growth Duration.'); return null; }
    if (yFrom > yTo)    { toast('Yield From must be less than or equal to Yield To.'); return null; }
    return { minGP, maxGP, yFrom, yTo };
  };

  // ── Add variant ───────────────────────────────────────────────────────────────
  const handleAddVariant = () => {
    const data   = getValues();
    const nums   = validateVariantFields(data);
    if (!nums) return;
    const { minGP, maxGP, yFrom, yTo } = nums;

    // The index this variant will have in the array
    const newIndex = variants.length;

    const newVariant = {
      VariantName:      data.variantName,
      VariantShortName: data.variantShortName,
      Description:      data.variantDescription,
      FeatureImage:     cvFeatureImage ? cvFeatureImage.name : 'default-variant.jpg',
      Significance:     significanceList.join('$') || 'Not specified',
      MinGrowthPeriod:  minGP,
      MaxGrowthPeriod:  maxGP,
      YieldFrom:        yFrom,
      YieldTo:          yTo,
      PhaseDet:         JSON.stringify(phases),
      AllowedCountries: variantCountries[newIndex] || [],
      isActive:         false,
    };

    setVariants(prev => [...prev, newVariant]);

    // Persist images for this variant index
    if (cvFeatureImage) {
      setVariantFeatureImages(prev  => ({ ...prev,  [newIndex]: cvFeatureImage }));
      setVariantFeatureImagesPreviews(prev => ({ ...prev, [newIndex]: cvFeatureImagePreview }));
    }
    if (cvOtherImages.length) {
      setVariantOtherImages(prev  => ({ ...prev,  [newIndex]: cvOtherImages.slice() }));
      setVariantOtherImagesPreviews(prev => ({ ...prev, [newIndex]: cvOtherImagesPreviews.slice() }));
    }

    // OL bootstrap
    setVariantsOL(prev => [...(prev || []), {
      VariantName:         { en: data.variantName },
      VariantShortName:    { en: data.variantShortName },
      VariantDescription:  { en: data.variantDescription },
      VariantSignificance: { en: newVariant.Significance },
    }]);

    resetVariantForm();
    // Clear the country selection slot for the "next new" variant (newIndex+1 would be next)
    // No cleanup needed – the slot for newIndex is already committed
    setMessage({ type: 'success', text: 'Variety added successfully!' });
    setTimeout(() => setMessage(null), 3000);
    setHasTranslated(false); setMainTab('english');
  };

  // ── Edit variant ──────────────────────────────────────────────────────────────
  const handleEditVariant = (index) => {
    const variant = variants[index];
    if (!variant) return;

    setValue('variantName',      variant.VariantName      || '');
    setValue('variantShortName', variant.VariantShortName || '');
    setValue('variantDescription', variant.Description    || '');
    setValue('minGrowthPeriod',  variant.MinGrowthPeriod  || '');
    setValue('maxGrowthPeriod',  variant.MaxGrowthPeriod  || '');
    setValue('yieldFrom',        variant.YieldFrom        || '');
    setValue('yieldTo',          variant.YieldTo          || '');

    // Significance
    const sigArr = variant.Significance
      ? (typeof variant.Significance === 'string'
          ? variant.Significance.split('$').filter(s => s.trim())
          : Array.isArray(variant.Significance) ? variant.Significance : [])
      : [];
    setSignificanceList(sigArr);

    // Phases
    let parsedPhases = [];
    try {
      const pd = variant.PhaseDet || '';
      if (pd.startsWith('[')) { parsedPhases = JSON.parse(pd); }
      else {
        pd.split('->').map(p => p.trim()).filter(Boolean).forEach(part => {
          const m = part.match(/(.+?)\s*\((\d+)-(\d+)\s*days\)/i);
          if (m) parsedPhases.push({ PhaseId: null, PhaseName: m[1].trim(), FromDays: parseInt(m[2]), ToDays: parseInt(m[3]) });
        });
      }
    } catch { parsedPhases = []; }
    setPhases(parsedPhases);

    // Images – restore from saved maps
    setCvFeatureImage(variantFeatureImages[index] || null);
    setCvFeatureImagePreview(variantFeatureImagesPreviews[index] || null);
    setCvOtherImages(variantOtherImages[index] ? variantOtherImages[index].slice() : []);
    setCvOtherImagesPreviews(variantOtherImagesPreviews[index] ? variantOtherImagesPreviews[index].slice() : []);

    // Countries: already in variantCountries[index] from initial load or previous edits
    // Nothing extra needed – the checkbox reads variantCountries[editingVariantIndex]

    setEditingVariantIndex(index);
    setMainTab('english');
  };

  // ── Update variant ────────────────────────────────────────────────────────────
  const handleUpdateVariant = () => {
    const data = getValues();
    const nums = validateVariantFields(data);
    if (!nums || editingVariantIndex === null) return;
    const { minGP, maxGP, yFrom, yTo } = nums;

    const updated = {
      ...variants[editingVariantIndex],
      VariantName:      data.variantName,
      VariantShortName: data.variantShortName,
      Description:      data.variantDescription,
      FeatureImage:     cvFeatureImage
        ? (cvFeatureImage.name || cvFeatureImage)
        : variants[editingVariantIndex].FeatureImage,
      Significance:     significanceList.join('$') || 'Not specified',
      MinGrowthPeriod:  minGP,
      MaxGrowthPeriod:  maxGP,
      YieldFrom:        yFrom,
      YieldTo:          yTo,
      PhaseDet:         JSON.stringify(phases),
      // ✅ FIX: read from variantCountries[editingVariantIndex], which the checkboxes write to
      AllowedCountries: variantCountries[editingVariantIndex] || variants[editingVariantIndex]?.AllowedCountries || [],
    };

    setVariants(prev => { const c = [...prev]; c[editingVariantIndex] = updated; return c; });

    // Update OL
    setVariantsOL(prev => {
      const c = [...(prev || [])];
      c[editingVariantIndex] = {
        ...(c[editingVariantIndex] || {}),
        VariantName:         { ...(c[editingVariantIndex]?.VariantName || {}),         en: data.variantName },
        VariantShortName:    { ...(c[editingVariantIndex]?.VariantShortName || {}),    en: data.variantShortName },
        VariantDescription:  { ...(c[editingVariantIndex]?.VariantDescription || {}),  en: data.variantDescription },
        VariantSignificance: { ...(c[editingVariantIndex]?.VariantSignificance || {}), en: updated.Significance },
      };
      return c;
    });

    // ✅ FIX: persist images under the SAME index (not shifting)
    if (cvFeatureImage) {
      setVariantFeatureImages(prev  => ({ ...prev,  [editingVariantIndex]: cvFeatureImage }));
      setVariantFeatureImagesPreviews(prev => ({ ...prev, [editingVariantIndex]: cvFeatureImagePreview }));
    }
    if (cvOtherImages.length) {
      setVariantOtherImages(prev  => ({ ...prev,  [editingVariantIndex]: cvOtherImages.slice() }));
      setVariantOtherImagesPreviews(prev => ({ ...prev, [editingVariantIndex]: cvOtherImagesPreviews.slice() }));
    }

    setMessage({ type: 'success', text: 'Variety updated successfully!' });
    setTimeout(() => setMessage(null), 3000);
    handleCancelEdit();
  };

  const handleCancelEdit = () => { setEditingVariantIndex(null); resetVariantForm(); };

  // ── Remove variant ────────────────────────────────────────────────────────────
  const handleRemoveVariant = (index) => {
    setVariants(prev => prev.filter((_, i) => i !== index));
    setVariantsOL(prev => (prev || []).filter((_, i) => i !== index));

    revokeBlob(variantFeatureImagesPreviews[index]);
    (variantOtherImagesPreviews[index] || []).forEach(revokeBlob);

    setVariantFeatureImages(prev         => reindexMap(prev, index));
    setVariantFeatureImagesPreviews(prev => reindexMap(prev, index));
    setVariantOtherImages(prev           => reindexMap(prev, index));
    setVariantOtherImagesPreviews(prev   => reindexMap(prev, index));
    setVariantCountries(prev             => reindexMap(prev, index));

    setHasTranslated(false); setMainTab('english');
  };

  // ── Translation ───────────────────────────────────────────────────────────────
  const onTranslateClick = async () => {
    if (!languages.length) { toast('Languages not loaded yet.'); return; }
    const vals = getValues();
    setStep('translating');
    setMessage({ type: 'info', text: 'Translating… please wait.' });

    try {
      // 1. Crop fields
      const cropPayload = {
        Category: vals.category || '', CropName: vals.cropName || '',
        CropShortName: vals.cropShortName || '', Description: vals.description || '',
      };
      significanceList.forEach((s, i) => { cropPayload[`Significance_${i}`] = s; });
      const cropRes  = await translateData(cropPayload);
      const cropData = cropRes?.data || cropRes || {};

      const ensureMap = (mapOrValue, original) => {
        const out = {};
        languages.forEach(({ GoogleLanguageCode: lc }) => { out[lc] = mapOrValue?.[lc] ?? original; });
        return out;
      };

      setCategoryOL(ensureMap(cropData.Category,      vals.category));
      setCropNameOL(ensureMap(cropData.CropName,       vals.cropName));
      setCropShortNameOL(ensureMap(cropData.CropShortName, vals.cropShortName));
      setDescriptionOL(ensureMap(cropData.Description, vals.description));

      // 2. Variants in chunks of 5
      const allVariantsOL = [];
      for (let ci = 0; ci < variants.length; ci += 5) {
        const chunk = variants.slice(ci, ci + 5);
        setMessage({ type: 'info', text: `Translating varieties ${ci + 1}–${ci + chunk.length} of ${variants.length}…` });
        const payload = {};
        chunk.forEach((v, i) => {
          payload[`VariantName_${i}`]        = v.VariantName   || '';
          payload[`VariantShortName_${i}`]   = v.VariantShortName || '';
          payload[`VariantDescription_${i}`] = v.Description   || '';
          payload[`VariantSignificance_${i}`]= v.Significance  || '';
        });
        const res  = await translateData(payload);
        const data = res?.data || res || {};
        chunk.forEach((v, i) => {
          allVariantsOL.push({
            VariantName:         ensureMap(data[`VariantName_${i}`],        v.VariantName),
            VariantShortName:    ensureMap(data[`VariantShortName_${i}`],   v.VariantShortName),
            VariantDescription:  ensureMap(data[`VariantDescription_${i}`], v.Description),
            VariantSignificance: ensureMap(data[`VariantSignificance_${i}`],v.Significance),
          });
        });
      }

      setVariantsOL(allVariantsOL);
      setHasTranslated(true);
      setMainTab('other');
      setMessage({ type: 'success', text: 'Translation completed!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Translation failed. Please try again.' });
    } finally {
      setStep('idle');
    }
  };

  // ── OtherLang field helpers ───────────────────────────────────────────────────
  const setVariantOLField = (index, field, lang, value) => {
    setVariantsOL(prev => {
      const copy = [...(prev || [])];
      const row  = { ...(copy[index] || { VariantName: {}, VariantShortName: {}, VariantDescription: {}, VariantSignificance: {} }) };
      row[field] = updateLangMap(row[field], lang, value);
      copy[index] = row;
      return copy;
    });
  };

  const buildOtherLangFormData = (fd) => {
    fd.append('CategoryOtherLang',    JSON.stringify(categoryOL    || {}));
    fd.append('CropNameOtherLang',    JSON.stringify(cropNameOL    || {}));
    fd.append('CropShortNameOtherLang', JSON.stringify(cropShortNameOL || {}));
    fd.append('DescriptionOtherLang', JSON.stringify(descriptionOL || {}));

    const sigOL = {};
    Object.keys(significanceOL || {}).forEach(lang => {
      sigOL[lang] = Array.isArray(significanceOL[lang])
        ? significanceOL[lang].join('$')
        : (significanceOL[lang] || '');
    });
    fd.append('SignificanceOtherLang', JSON.stringify(sigOL));
    fd.append('VariantsOtherLang',     JSON.stringify(
      (variantsOL || []).map(v => ({
        VariantName:         v.VariantName         || {},
        VariantShortName:    v.VariantShortName    || {},
        VariantDescription:  v.VariantDescription  || {},
        VariantSignificance: v.VariantSignificance || {},
      }))
    ));
  };

  // ── Form submit ───────────────────────────────────────────────────────────────
  const onFormSubmit = async (data) => {
    if (!data.category?.trim())      { toast('Category is required'); return; }
    if (!data.cropName?.trim())      { toast('Crop Name is required'); return; }
    if (!data.cropShortName?.trim()) { toast('Crop Short Name is required'); return; }
    if (!data.description?.trim())   { toast('Description is required'); return; }
    if (!featureImage && !cropToEdit){ setMessage({ type: 'error', text: 'Feature Image is required' }); return; }

    try {
      setLoading(true);
      const token    = getToken();
      const formData = new FormData();

      // Crop images
      if (featureImage) formData.append('FeatureImage', featureImage);
      otherImages.forEach(f => formData.append('OtherImages', f));

      // Basic fields
      formData.append('Category',      data.category);
      formData.append('CropName',      data.cropName);
      formData.append('CropShortName', data.cropShortName);
      formData.append('Description',   data.description);
      formData.append('IsActive',      data.isActive.toString());
      formData.append('AllowedCountries', JSON.stringify(selectedCountries));

      buildOtherLangFormData(formData);
      if (cropToEdit) formData.append('ID', cropToEdit.ID);

      if (cropToEdit) {
        // ── UPDATE: structured variant diff ────────────────────────────────────
        const variantsData = { added: [], updated: [], deleted: [] };
        // addedImageIndexes tracks which slot in "added[]" each file belongs to
        const addedFeatureImages = [];
        const addedOtherImages   = [];   // [{ file, addedIdx }]

        variants.forEach((variant, idx) => {
          const variantOL   = variantsOL[idx] || {};
          const variantData = {
            VariantName:               variant.VariantName,
            VariantNameOtherLang:      JSON.stringify(variantOL.VariantName         || { en: variant.VariantName }),
            VariantShortName:          variant.VariantShortName,
            VariantShorNameOtherLang:  JSON.stringify(variantOL.VariantShortName    || { en: variant.VariantShortName }),
            Description:               variant.Description,
            DescriptionOtherLang:      JSON.stringify(variantOL.VariantDescription  || { en: variant.Description }),
            FeatureImage:              variant.FeatureImage,
            Significance:              variant.Significance,
            SignificanceOtherLang:     JSON.stringify(variantOL.VariantSignificance || { en: variant.Significance }),
            MinGrowthPeriod:           variant.MinGrowthPeriod,
            MaxGrowthPeriod:           variant.MaxGrowthPeriod,
            YieldFrom:                 variant.YieldFrom,
            YieldTo:                   variant.YieldTo,
            PhaseDet:                  variant.PhaseDet,
            // ✅ FIX: use persisted variantCountries[idx], not stale AllowedCountries
            AllowedCountries: JSON.stringify(
              Array.isArray(variantCountries[idx])
                ? variantCountries[idx]
                : (Array.isArray(variant.AllowedCountries) ? variant.AllowedCountries : [])
            ),
            isActive: variant.isActive ?? true,
          };

          if (variant.ID) {
            variantData.ID = variant.ID;
            variantsData.updated.push(variantData);
            const updIdx = variantsData.updated.length - 1;

            // ✅ FIX: feature image for updated variants
            const featFile = variantFeatureImages[idx];
            if (featFile instanceof File) {
              formData.append('VariantFeatureImages', featFile);
              formData.append('VariantFeatureImageIndex', updIdx.toString());
              formData.append('VariantFeatureImageIsUpdate', 'true');
            }
            // ✅ FIX: other images for updated variants
            const otherFiles = variantOtherImages[idx];
            if (Array.isArray(otherFiles)) {
              otherFiles.forEach(f => {
                if (f instanceof File) {
                  formData.append('VariantOtherImages', f);
                  formData.append('VariantOtherImageIndex', updIdx.toString());
                  formData.append('VariantOtherImageIsUpdate', 'true');
                }
              });
            }
          } else {
            variantsData.added.push(variantData);
            const addIdx = variantsData.added.length - 1;

            // ✅ FIX: feature image keyed to correct added index
            const featFile = variantFeatureImages[idx];
            if (featFile instanceof File) {
              formData.append('VariantFeatureImages', featFile);
              formData.append('VariantFeatureImageIndex', addIdx.toString());
            }
            // ✅ FIX: other images keyed to correct added index
            const otherFiles = variantOtherImages[idx];
            if (Array.isArray(otherFiles)) {
              otherFiles.forEach(f => {
                if (f instanceof File) {
                  formData.append('VariantOtherImages', f);
                  formData.append('VariantOtherImageIndex', addIdx.toString());
                }
              });
            }
          }
        });

        // Deleted variants
        if (cropToEdit?.Variants) {
          const currentIds = new Set(variants.map(v => v.ID).filter(Boolean));
          variantsData.deleted = cropToEdit.Variants.map(v => v.ID).filter(id => !currentIds.has(id));
        }

        formData.append('VariantDetails', JSON.stringify(variantsData));
        await updateCrop(formData, token);
      } else {
        // ── ADD: flat variant array + images ────────────────────────────────────
        formData.append('VariantDetails', JSON.stringify(variants));
        variants.forEach((_, idx) => {
          const featFile = variantFeatureImages[idx];
          if (featFile instanceof File) {
            formData.append('VariantFeatureImages', featFile);
            formData.append('VariantFeatureImageIndex', idx.toString());
          }
          const otherFiles = variantOtherImages[idx];
          if (Array.isArray(otherFiles)) {
            otherFiles.forEach(f => {
              if (f instanceof File) {
                formData.append('VariantOtherImages', f);
                formData.append('VariantOtherImageIndex', idx.toString());
              }
            });
          }
        });
        await addCrop(formData, token);
      }

      setMessage({ type: 'success', text: cropToEdit ? 'Crop updated successfully!' : 'Crop added successfully!' });
      onCropAdded?.();
      setTimeout(() => { setMessage(null); fullReset(); onClose(); }, 2000);
    } catch (error) {
      console.error('Error saving crop:', error);
      toast(error?.response?.data?.message || `Failed to ${cropToEdit ? 'update' : 'save'} crop. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  // Index the checkboxes use for the "current" variant
  const activeVariantIndex = editingVariantIndex !== null ? editingVariantIndex : variants.length;

  return (
    <Drawer open={isOpen} direction="right" size={900} style={{ zIndex: 1050 }} className="add-crop-master">
      <div className="relative">
        <button type="button" className="close-btn bg-transparent text-secondary-light hover:text-primary-600"
          onClick={onClose} title="Close" aria-label="Close">
          <Icon icon="lucide:x" className="text-2xl" />
        </button>

        <div className="card-body">
          <h6 className="text-xxl text-primary-light mb-16">{cropToEdit ? 'Edit Crop' : 'Add New Crop'}</h6>

          {/* Main Tabs */}
          <div className="tabs-row">
            <button type="button" className={`main-tab ${mainTab === 'english' ? 'active' : ''}`} onClick={() => setMainTab('english')}>
              English
            </button>
            <button type="button" className={`main-tab ${mainTab === 'other' ? 'active' : ''}`}
              onClick={() => setMainTab('other')}
              disabled={!cropToEdit && !hasTranslated}
              title={!hasTranslated ? 'Translate first to enable' : 'Other Languages'}>
              Other Languages
            </button>
          </div>

          {message && (
            <div className={message.type === 'success' ? 'success-message' : 'error-message'}>{message.text}</div>
          )}
          {loading && <div className="text-center py-2"><span>Processing…</span></div>}

          <form onSubmit={handleSubmit(onFormSubmit)}>
            {/* ════════════════════ ENGLISH TAB ════════════════════ */}
            {mainTab === 'english' && (
              <>
                {/* ── Crop Details ── */}
                <div className="section">
                  <h6 className="text-md text-primary-light mb-12">Crop Details</h6>

                  <div className="mb-20">
                    <label htmlFor="category" className="form-label">Category <span className="text-danger-600">*</span></label>
                    <select id="category" className={`form-control ${errors.category ? 'border-danger-600' : ''}`}
                      {...register('category', { required: 'Category is required' })}>
                      <option value="">Select Category</option>
                      {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {errors.category && <span className="error-message">{errors.category.message}</span>}
                  </div>

                  <div className="form-grid-container">
                    <div>
                      <label htmlFor="cropName" className="form-label">Crop Name <span className="text-danger-600">*</span></label>
                      <input type="text" id="cropName" className={`form-control ${errors.cropName ? 'border-danger-600' : ''}`}
                        placeholder="Enter Crop Name"
                        {...register('cropName', {
                          required: 'Crop Name is required',
                          minLength: { value: 2, message: 'At least 2 characters' },
                          pattern: { value: /^[A-Za-z\s]+$/, message: 'Letters and spaces only' },
                        })}
                        ref={e => { register('cropName').ref(e); cropInputRef.current = e; }} />
                      {errors.cropName && <span className="error-message">{errors.cropName.message}</span>}
                    </div>
                    <div>
                      <label htmlFor="cropShortName" className="form-label">Crop Short Name <span className="text-danger-600">*</span></label>
                      <input type="text" id="cropShortName" className={`form-control ${errors.cropShortName ? 'border-danger-600' : ''}`}
                        placeholder="Enter Crop Short Name"
                        {...register('cropShortName', {
                          required: 'Crop Short Name is required',
                          maxLength: { value: 10, message: 'Max 10 characters' },
                        })} />
                      {errors.cropShortName && <span className="error-message">{errors.cropShortName.message}</span>}
                    </div>
                  </div>

                  <div className="mb-20">
                    <label htmlFor="description" className="form-label">Description <span className="text-danger-600">*</span></label>
                    <textarea id="description" className={`form-control ${errors.description ? 'border-danger-600' : ''}`}
                      placeholder="Enter Description"
                      {...register('description', {
                        required: 'Description is required',
                        maxLength: { value: 1000, message: 'Max 1000 characters' },
                      })} />
                    {errors.description && <span className="error-message">{errors.description.message}</span>}
                  </div>

                  {/* Crop-level countries */}
                  <div className="mb-20">
                    <label className="form-label">Allowed Countries <span className="text-danger-600">*</span></label>
                    <div className="d-flex flex-wrap gap-3 mt-2">
                      {countries.map(c => (
                        <label key={c.ID} className="form-check style-check">
                          <input type="checkbox" className="form-check-input"
                            checked={selectedCountries.includes(c.ID)}
                            onChange={e => setSelectedCountries(prev =>
                              e.target.checked ? [...prev, c.ID] : prev.filter(id => id !== c.ID)
                            )} />
                          <span>{c.CountryName}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Feature image */}
                  <div className="mb-20">
                    <label htmlFor="featureImage" className="form-label">Feature Image <span className="text-danger-600">*</span></label>
                    <input type="file" id="featureImage" className="form-control" accept="image/*"
                      onChange={handleFeatureImageChange} />
                    <small>Image size should be less than 1MB</small>
                    {featureImagePreview && (
                      <div className="image-preview-container">
                        <span className="image-preview-title">Preview:</span>
                        <div className="image-preview-grid">
                          <div className="image-preview-item">
                            <img src={featureImagePreview} alt="Feature preview" className="image-preview" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Other images */}
                  <div className="mb-20">
                    <label htmlFor="otherImages" className="form-label">Other Images (max {MAX_OTHER_IMAGES})</label>
                    <input type="file" id="otherImages" className="form-control" accept="image/*" multiple
                      onChange={handleOtherImagesChange} />
                    <small>Image size should be less than 1MB</small>
                    <div className="text-sm text-neutral-600 mt-1">{otherImages.length}/{MAX_OTHER_IMAGES} images selected</div>
                    {otherImagesPreviews.length > 0 && (
                      <div className="image-preview-container">
                        <span className="image-preview-title">Preview:</span>
                        <div className="image-preview-grid">
                          {otherImagesPreviews.map((src, i) => (
                            <div key={i} className="image-preview-item">
                              <img src={src} alt={`Other ${i + 1}`} className="image-preview" />
                              <button type="button" className="image-remove-btn" onClick={() => removeOtherImage(i)}>×</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-check style-check">
                    <input type="checkbox" id="isActive" className="form-check-input radius-4 border border-neutral-400"
                      {...register('isActive')} />
                    <label htmlFor="isActive" className="form-label">Is Active</label>
                  </div>
                </div>

                {/* ── Variety ── */}
                <div className="section">
                  <h6 className="text-md text-primary-light mb-12">Variety</h6>

                  <div className="form-grid-container">
                    <div>
                      <label htmlFor="variantName" className="form-label">Variety Name <span className="text-danger-600">*</span></label>
                      <input type="text" id="variantName" className="form-control" placeholder="Enter Variety Name" {...register('variantName')} />
                    </div>
                    <div>
                      <label htmlFor="variantShortName" className="form-label">Variety Short Name <span className="text-danger-600">*</span></label>
                      <input type="text" id="variantShortName" className="form-control" placeholder="Enter Variety Short Name" {...register('variantShortName')} />
                    </div>
                  </div>

                  <div className="mb-20">
                    <label htmlFor="variantDescription" className="form-label">Variety Description <span className="text-danger-600">*</span></label>
                    <textarea id="variantDescription" className="form-control" placeholder="Enter Variety Description" {...register('variantDescription')} />
                  </div>

                  {/* ✅ FIX: Variant-level countries
                      Reads/writes variantCountries[activeVariantIndex]
                      activeVariantIndex = editingVariantIndex (edit) or variants.length (add) */}
                  <div className="mb-20">
                    <label className="form-label">Allowed Countries for this Variety</label>
                    {selectedCountries.length === 0 && (
                      <div className="text-danger-600 text-sm mt-1">Please select countries in Crop Details first.</div>
                    )}
                    <div className="d-flex flex-wrap gap-3 mt-2">
                      {selectedCountries.map(countryId => {
                        const c = countries.find(cc => cc.ID === countryId);
                        if (!c) return null;
                        return (
                          <label key={countryId} className="form-check style-check">
                            <input type="checkbox" className="form-check-input"
                              checked={(variantCountries[activeVariantIndex] || []).includes(countryId)}
                              onChange={e => setVariantCountries(prev => {
                                const current = prev[activeVariantIndex] || [];
                                return {
                                  ...prev,
                                  [activeVariantIndex]: e.target.checked
                                    ? [...current, countryId]
                                    : current.filter(id => id !== countryId),
                                };
                              })} />
                            <span>{c.CountryName}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Variant images */}
                  <div className="form-grid-container mb-20">
                    <div>
                      <label htmlFor="variantFeatureImage" className="form-label">Variety Feature Image</label>
                      <input type="file" id="variantFeatureImage" className="form-control" accept="image/*"
                        onChange={handleCvFeatureImageChange} />
                      <small>Image size should be less than 1MB</small>
                      {cvFeatureImagePreview && (
                        <div className="image-preview-container">
                          <span className="image-preview-title">Preview:</span>
                          <div className="image-preview-grid">
                            <div className="image-preview-item">
                              <img src={cvFeatureImagePreview} alt="Variety feature preview" className="image-preview" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label htmlFor="variantOtherImages" className="form-label">Variety Other Images (max {MAX_OTHER_IMAGES})</label>
                      <input type="file" id="variantOtherImages" className="form-control" accept="image/*" multiple
                        onChange={handleCvOtherImagesChange} />
                      <small>Image size should be less than 1MB</small>
                      <div className="text-sm text-neutral-600 mt-1">{cvOtherImages.length}/{MAX_OTHER_IMAGES} images selected</div>
                      {cvOtherImagesPreviews.length > 0 && (
                        <div className="image-preview-container">
                          <span className="image-preview-title">Preview:</span>
                          <div className="image-preview-grid">
                            {cvOtherImagesPreviews.map((src, i) => (
                              <div key={i} className="image-preview-item">
                                <img src={src} alt={`Variety other ${i + 1}`} className="image-preview" />
                                <button type="button" className="image-remove-btn" onClick={() => removeCvOtherImage(i)}>×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Significance */}
                  <div className="mb-20">
                    <label htmlFor="significance" className="form-label">Significance</label>
                    <textarea id="significance" className="form-control" placeholder="Enter Significance" {...register('significance')} />
                    <button type="button" className="btn btn-green add-button" onClick={handleAddSignificance}>Add Significance</button>
                  </div>
                  {significanceList.length > 0 && (
                    <table className="table mb-20">
                      <thead><tr><th>Significance</th><th>Action</th></tr></thead>
                      <tbody>
                        {significanceList.map((sig, i) => (
                          <tr key={i}>
                            <td>{sig}</td>
                            <td>
                              <button type="button" className="delete-btn border-0 bg-danger-focus text-danger-600 bg-hover-danger-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => handleRemoveSignificance(i)}>
                                <Icon icon="lucide:trash-2" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* ── Growth Details ── */}
                <div className="section">
                  <h6 className="text-md text-primary-light mb-12">Growth Details</h6>

                  <div className="form-grid-container">
                    <div>
                      <label htmlFor="minGrowthPeriod" className="form-label">Min Growth Period (days) <span className="text-danger-600">*</span></label>
                      <input type="number" id="minGrowthPeriod" className="form-control" placeholder="Min days"
                        {...register('minGrowthPeriod', { required: true, min: 1 })} />
                    </div>
                    <div>
                      <label htmlFor="maxGrowthPeriod" className="form-label">Max Growth Period (days) <span className="text-danger-600">*</span></label>
                      <input type="number" id="maxGrowthPeriod" className="form-control" placeholder="Max days"
                        {...register('maxGrowthPeriod', { required: true, min: 1 })} />
                    </div>
                  </div>

                  <div className="form-grid-container mb-20">
                    <div>
                      <label htmlFor="phase" className="form-label">Phase <span className="text-danger-600">*</span></label>
                      <select id="phase" className="form-control" {...register('phase')}>
                        <option value="">Select Phase</option>
                        {phasesList.map(p => <option key={p.ID} value={p.ID}>{p.PhaseName}</option>)}
                      </select>
                    </div>
                    <div className="form-grid-container">
                      <div>
                        <label htmlFor="fromDay" className="form-label">From Day <span className="text-danger-600">*</span></label>
                        <input type="number" id="fromDay" className="form-control" placeholder="From" {...register('fromDay', { min: 1 })} />
                      </div>
                      <div>
                        <label htmlFor="toDay" className="form-label">To Day <span className="text-danger-600">*</span></label>
                        <input type="number" id="toDay" className="form-control" placeholder="To" {...register('toDay', { min: 1 })} />
                      </div>
                    </div>
                  </div>

                  <button type="button" className="btn btn-green mb-20" onClick={handleAddPhase}>Add Phase Details</button>

                  {phases.length > 0 && (
                    <table className="table mb-20">
                      <thead><tr><th>Phase</th><th>From</th><th>To</th><th>Action</th></tr></thead>
                      <tbody>
                        {phases.map((p, i) => (
                          <tr key={i}>
                            <td>{p.PhaseName}</td><td>{p.FromDays}</td><td>{p.ToDays}</td>
                            <td>
                              <button type="button" className="delete-btn border-0 bg-danger-focus text-danger-600 bg-hover-danger-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                                onClick={() => handleRemovePhase(i)}>
                                <Icon icon="lucide:trash-2" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div className="form-grid-container mb-20">
                    <div>
                      <label htmlFor="yieldFrom" className="form-label">Yield (T/Ha) From <span className="text-danger-600">*</span></label>
                      <input type="number" step="0.1" id="yieldFrom" className="form-control" placeholder="From"
                        {...register('yieldFrom', { required: true, min: 0 })} />
                    </div>
                    <div>
                      <label htmlFor="yieldTo" className="form-label">Yield (T/Ha) To <span className="text-danger-600">*</span></label>
                      <input type="number" step="0.1" id="yieldTo" className="form-control" placeholder="To"
                        {...register('yieldTo', { required: true, min: 0 })} />
                    </div>
                  </div>

                  {/* Add / Update / Cancel buttons */}
                  <button type="button" className="btn btn-green mb-20"
                    onClick={handleAddVariant} disabled={editingVariantIndex !== null}>
                    Add Variety
                  </button>
                  {editingVariantIndex !== null && (
                    <>
                      <button type="button" className="btn btn-primary-500 mx-12 mb-20" onClick={handleUpdateVariant}>Update Variant</button>
                      <button type="button" className="btn btn-yellow ml-12 mb-20" onClick={handleCancelEdit}>Undo</button>
                    </>
                  )}

                  {/* Variants table */}
                  {variants.length > 0 && (
                    <table className="table mb-20">
                      <thead>
                        <tr>
                          <th>Variety Name</th><th>Short Name</th><th>Description</th>
                          <th>Feature Image</th><th>Other Images</th><th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {variants.map((v, i) => (
                          <tr key={i}>
                            <td>{v.VariantName}</td>
                            <td>{v.VariantShortName}</td>
                            <td>{v.Description}</td>
                            <td>
                              {variantFeatureImagesPreviews[i] && (
                                <img src={variantFeatureImagesPreviews[i]} alt="feature" className="small-image-preview" />
                              )}
                            </td>
                            <td>
                              {variantOtherImagesPreviews[i]?.length > 0 && (
                                <div className="image-preview-grid">
                                  {variantOtherImagesPreviews[i].map((src, j) => (
                                    <img key={j} src={src} alt={`other-${j}`} className="small-image-preview" />
                                  ))}
                                </div>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <button type="button"
                                  className="edit-btn border-0 bg-success-focus text-success-600 bg-hover-success-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                                  onClick={() => handleEditVariant(i)}>
                                  <Icon icon="lucide:edit" />
                                </button>
                                <button type="button"
                                  className="delete-btn border-0 bg-danger-focus text-danger-600 bg-hover-danger-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                                  onClick={() => handleRemoveVariant(i)}>
                                  <Icon icon="lucide:trash-2" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Bottom actions */}
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button type="button"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                    onClick={onClose} disabled={loading}>
                    Cancel
                  </button>
                  <button type="button"
                    className="btn btn-yellow border border-600 text-md px-56 py-12 radius-8"
                    onClick={onTranslateClick}
                    disabled={step === 'translating' || !languages.length}>
                    {step === 'translating' ? 'Translating…' : 'Translate'}
                  </button>
                  <button type="submit"
                    className="btn btn-green border border-600 text-md px-56 py-12 radius-8"
                    disabled={loading || !hasTranslated}>
                    {loading ? 'Saving…' : cropToEdit ? 'Update' : 'Save'}
                  </button>
                </div>
              </>
            )}

            {/* ════════════════════ OTHER LANGUAGES TAB ════════════════════ */}
            {mainTab === 'other' && (
              <>
                <div className="lang-tabs">
                  {languages.length === 0
                    ? <span style={{ opacity: 0.7 }}>No languages loaded</span>
                    : languages.map(lang => (
                      <button key={lang.GoogleLanguageCode} type="button"
                        className={`lang-tab ${selectedLang === lang.GoogleLanguageCode ? 'active' : ''}`}
                        onClick={() => setSelectedLang(lang.GoogleLanguageCode)}>
                        {lang.LanguageName}
                      </button>
                    ))
                  }
                </div>

                {/* Crop fields */}
                <div className="section">
                  <h6 className="text-md text-primary-light mb-12">Crop ({selectedLangName})</h6>
                  <div className="mb-20">
                    <label className="form-label">Category</label>
                    <input type="text" className="form-control" placeholder="Translated Category"
                      value={categoryOL?.[selectedLang] || ''}
                      onChange={e => setCategoryOL(prev => updateLangMap(prev, selectedLang, e.target.value))} />
                  </div>
                  <div className="form-grid-container">
                    <div>
                      <label className="form-label">Crop Name</label>
                      <input type="text" className="form-control" placeholder="Translated Crop Name"
                        value={cropNameOL?.[selectedLang] || ''}
                        onChange={e => setCropNameOL(prev => updateLangMap(prev, selectedLang, e.target.value))} />
                    </div>
                    <div>
                      <label className="form-label">Crop Short Name</label>
                      <input type="text" className="form-control" placeholder="Translated Short Name"
                        value={cropShortNameOL?.[selectedLang] || ''}
                        onChange={e => setCropShortNameOL(prev => updateLangMap(prev, selectedLang, e.target.value))} />
                    </div>
                  </div>
                  <div className="mb-20">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" placeholder="Translated Description"
                      value={descriptionOL?.[selectedLang] || ''}
                      onChange={e => setDescriptionOL(prev => updateLangMap(prev, selectedLang, e.target.value))} />
                  </div>
                </div>

                {/* Variant OL */}
                <div className="section">
                  <h6 className="text-md text-primary-light mb-12">Varieties ({selectedLangName})</h6>
                  {variants.length === 0
                    ? <div style={{ opacity: 0.7 }}>No varieties added yet.</div>
                    : (
                      <table className="table">
                        <thead>
                          <tr>
                            <th>#</th><th>Variety Name</th><th>Short Name</th>
                            <th>Description</th><th>Significance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {variants.map((_, i) => {
                            const rowOL = variantsOL[i] || { VariantName: {}, VariantShortName: {}, VariantDescription: {}, VariantSignificance: {} };
                            const sigStr = rowOL.VariantSignificance?.[selectedLang] || '';
                            const sigItems = typeof sigStr === 'string' ? sigStr.split('$').filter(s => s.trim()) : [];
                            return (
                              <tr key={i}>
                                <td>{i + 1}</td>
                                <td>
                                  <input className="form-control" placeholder="Translated Name"
                                    value={rowOL.VariantName?.[selectedLang] || ''}
                                    onChange={e => setVariantOLField(i, 'VariantName', selectedLang, e.target.value)} />
                                </td>
                                <td>
                                  <input className="form-control" placeholder="Translated Short Name"
                                    value={rowOL.VariantShortName?.[selectedLang] || ''}
                                    onChange={e => setVariantOLField(i, 'VariantShortName', selectedLang, e.target.value)} />
                                </td>
                                <td>
                                  <textarea className="form-control" placeholder="Translated Description"
                                    value={rowOL.VariantDescription?.[selectedLang] || ''}
                                    onChange={e => setVariantOLField(i, 'VariantDescription', selectedLang, e.target.value)} />
                                </td>
                                <td>
                                  <div className="mb-8"><strong>Significance Items:</strong></div>
                                  {sigItems.map((item, j) => (
                                    <div key={j} className="flex items-center gap-2 mb-2">
                                      <input className="form-control" value={item}
                                        onChange={e => {
                                          const next = [...sigItems]; next[j] = e.target.value;
                                          setVariantOLField(i, 'VariantSignificance', selectedLang, next.join('$'));
                                        }} />
                                      <button type="button"
                                        className="delete-btn border-0 bg-danger-focus text-danger-600 bg-hover-danger-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                                        onClick={() => {
                                          const next = sigItems.filter((_, idx) => idx !== j);
                                          setVariantOLField(i, 'VariantSignificance', selectedLang, next.join('$'));
                                        }}>
                                        <Icon icon="lucide:trash-2" />
                                      </button>
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )
                  }
                </div>

                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button type="button"
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                    onClick={onClose} disabled={loading}>
                    Cancel
                  </button>
                  <button type="submit"
                    className="btn btn-yellow border border-600 text-md px-56 py-12 radius-8"
                    disabled={loading}>
                    {loading ? 'Saving…' : cropToEdit ? 'Update' : 'Save'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </Drawer>
  );
};

export default AddCropMaster;